import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import styled, { css } from 'styled-components';
import { useWakeLock } from '../../hooks/useWakeLock';
import { isSupportedBrowser, useHasSupportedVideoCodec, useShare, } from '../../utils';
import { getStreamUrl } from '../../utils/url';
import { Anchor } from '../Anchor';
import { CodecNotSupported, GenericBrowserNotSupported, } from '../BrowserNotSupported';
import { Button } from '../Button';
import { Canvas } from '../Canvas';
import { ShareIcon, SpeakerIcon } from '../Icon';
import { LiveIndicator } from '../LiveIndicator';
import { LoadingIndicator } from '../LoadingIndicator';
import { Stage } from '../Stage';
import SvgIcon from '../SvgIcon';
import { openViewerOptions } from '../popups/ViewerOptionsPopup';
import { BottomViewToolbar, StageContainer, StatusContainer, TopViewToolbar, ViewRoot, ViewToolbarSegment, } from './View';
export function ViewerView({ streamPartId }) {
    const [ready, setReady] = useReducer(() => true, false);
    const [isAudioOnMute, setIsAudioOnMute] = useState();
    const navigate = useNavigate();
    const { canShare, openSystemShareDialog } = useShare();
    const browserVideoCodecSupported = useHasSupportedVideoCodec();
    const canvasControllerRef = useRef(null);
    useWakeLock({ enabled: true });
    // biome-ignore lint/correctness/useExhaustiveDependencies: Track the time the user started watching *this* stream
    const watchingStartedAt = useMemo(() => {
        return new Date();
    }, [streamPartId]);
    const optionsButtonAnchorRef = useRef(null);
    if (!isSupportedBrowser()) {
        return _jsx(GenericBrowserNotSupported, {});
    }
    if (browserVideoCodecSupported === false) {
        return _jsx(CodecNotSupported, {});
    }
    return (_jsxs(_Fragment, { children: [_jsx(LoadingIndicator, { loading: !ready }), _jsxs(ViewRoot, { children: [_jsx(TopViewToolbar, { children: _jsx(StatusContainer, { children: _jsx(LiveIndicator, { streamPartId: streamPartId, startedAt: watchingStartedAt }) }) }), _jsx(StageContainer, { children: _jsx(Stage, { children: _jsx(Canvas, { controllerRef: canvasControllerRef, streamPartId: streamPartId, onMuteChange: setIsAudioOnMute, onReady: setReady, fit: "contain" }) }) }), _jsx(BottomViewToolbar, { children: _jsxs(Buttons, { "$canShare": canShare, children: [_jsx("div", {}), _jsx(Button, { type: "button", shape: "pill", padding: "none", kind: isAudioOnMute ? undefined : 'secondary', variant: isAudioOnMute ? undefined : 'dark', icon: _jsx(SpeakerIcon, { muted: isAudioOnMute }), onClick: () => {
                                        if (isAudioOnMute) {
                                            canvasControllerRef.current?.unmute();
                                        }
                                        else {
                                            canvasControllerRef.current?.mute();
                                        }
                                    } }), canShare && (_jsx(Button, { type: "button", kind: "secondary", shape: "pill", padding: "none", variant: "dark", icon: _jsx(ShareIcon, {}), onClick: () => {
                                        openSystemShareDialog(getStreamUrl(streamPartId));
                                    } })), _jsx(Anchor, { ref: optionsButtonAnchorRef, children: _jsx(Button, { type: "button", kind: "secondary", shape: "pill", padding: "none", variant: "dark", icon: _jsx(SvgIcon, { name: "dotDotDot" }), onClick: () => {
                                            openViewerOptions({
                                                anchorRef: optionsButtonAnchorRef,
                                            });
                                        } }) }), _jsx(Button, { type: "button", kind: 'destructive', shape: "pill", onClick: () => {
                                        navigate('/');
                                    }, children: "Leave" }), _jsx("div", {})] }) })] })] }));
}
const Buttons = styled(ViewToolbarSegment) `
    grid-template-columns: 1fr auto auto auto auto 1fr;

    ${({ $canShare }) => !$canShare &&
    css `
            grid-template-columns: 1fr auto auto auto 1fr;
        `}
`;
